/**
 * Generic Stages Slideout - Edit pipeline/lifecycle stages
 *
 * Provides a slideout for editing stages with drag-and-drop
 * reordering, inline editing, and add/delete capabilities.
 *
 * Works with both deal pipeline stages and contact lifecycle stages.
 * Uses StageListEditor for the shared stage list layout.
 */

import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RefreshCw01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { InputBase } from '@/lib/base/forms/input/input';
import { InputGroup, InputPrefix } from '@/lib/base/forms/input/input-group';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { TextArea } from '@/lib/base/forms/textarea/textarea';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import HorizontalTabs from '@/lib/base/tabs/HorizontalTabs';
import {
  buildNewStageTerminalPayload,
  createIsTerminalColumn,
} from '@/lib/crm/components/stages/stageColumns';
import { StageDescriptionField } from '@/lib/crm/components/stages/StageDescriptionField';
import type {
  StageColumn,
  StageItem,
  StageValidationErrors,
} from '@/lib/crm/components/stages/StageListEditor';
import { StageListEditor } from '@/lib/crm/components/stages/StageListEditor';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { useFieldTypes } from '@/lib/crm/hooks/useFieldTypes';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import {
  CardFieldsEditorBody,
  type CardLayoutTabProps,
} from '@/lib/crm/pipeline/components/CardFieldsEditorBody';
import {
  StageFooterEditorBody,
  type StageFooterTabProps,
} from '@/lib/crm/pipeline/components/StageFooterEditorBody';
import { useCardFieldsDraft } from '@/lib/crm/pipeline/hooks/useCardFieldsDraft';
import { usePipelineCardConfig } from '@/lib/crm/pipeline/hooks/usePipelineCardConfig';
import { usePipelineFooterConfig } from '@/lib/crm/pipeline/hooks/usePipelineFooterConfig';
import { useStageFooterDraft } from '@/lib/crm/pipeline/hooks/useStageFooterDraft';
import { buildDefaultCardConfig } from '@/lib/crm/pipeline/utils/cardConfigDefaults';
import {
  buildFieldPickerOptions,
  buildSupplementalPropertyOptions,
} from '@/lib/crm/pipeline/utils/cardConfigOptions';
import { buildDefaultFooterConfig } from '@/lib/crm/pipeline/utils/stageFooterConfig';
import { apiPut } from '@/lib/http/client';
import { sanitizeError } from '@/lib/utils/errors';
import { useViewHealthScope } from '@/lib/view-health';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

/**
 * Configuration for editing stages of a pipeline-tier entity type
 * (e.g. deal pipelines). Stages are scoped to a specific pipeline entity.
 */
export interface PipelineStagesSlideoutConfig {
  /** Discriminator: stages live under a pipeline object. */
  mode: 'pipeline';
  /** Stage entity type name (e.g., 'ahoy_deal_pipeline_stage') */
  stageEntityTypeName: string;
  /** Pipeline name to filter stages (e.g., 'Sales Pipeline') */
  pipelineName: string;
  /** Association type for checking stage usage */
  associationTypeName: string;
  /** Object type that uses these stages (for usage check) */
  targetObjectTypeName: string;
  /** Whether to show probability field */
  showProbability: boolean;
  /** Title for the slideout */
  title: string;
  /** Description for the slideout */
  description: string;
}

/**
 * Configuration for editing stages of a stage-only entity type (no pipeline
 * tier — e.g. ``ahoy_contact_lifecycle_stage``). Stages are listed by
 * ``stageEntityTypeName`` and saved through the dedicated ``by-stage-type``
 * endpoint.
 */
export interface StageOnlyStagesSlideoutConfig {
  /** Discriminator: stages have no pipeline parent. */
  mode: 'stage_only';
  /** Stage entity type name (e.g., 'ahoy_contact_lifecycle_stage') */
  stageEntityTypeName: string;
  /** Direct base→stage association type (used for usage checks) */
  associationTypeName: string;
  /** Base entity type that associates with the stage (e.g., 'ahoy_contact') */
  targetObjectTypeName: string;
  /** Whether to show probability field (rare for stage-only taxonomies) */
  showProbability: boolean;
  /** Title for the slideout */
  title: string;
  /** Description for the slideout */
  description: string;
}

/**
 * Discriminated union of stages slideout configurations.
 *
 * The two arms map to fundamentally different data shapes — pipeline-tier
 * stages are loaded via ``associated_with_entity_id`` against a parent
 * pipeline object, while stage-only taxonomies are loaded by entity type
 * alone. Keeping them as separate types makes the schema difference
 * explicit at the call site.
 */
export type StagesSlideoutConfig =
  | PipelineStagesSlideoutConfig
  | StageOnlyStagesSlideoutConfig;

export interface StagesSlideoutProps {
  /** Configuration for the stages */
  config: StagesSlideoutConfig;
  /** Whether the slideout is open */
  isOpen: boolean;
  /** Callback when the slideout is closed */
  onClose: () => void;
  /** Callback when the operation succeeds */
  onSuccess?: () => void;
  /** Override pipeline entity_id (e.g., when viewing a non-default pipeline) */
  pipelineId?: string;
  /**
   * Singular human-readable label for the entity type (e.g. "Deal", "Note").
   * Used in the card layout preview title ("Example Note"). Defaults to "Deal"
   * when omitted for backwards compatibility.
   */
  entityLabel?: string;
  /** Initial pipeline name for inline rename (optional — omit to hide name field) */
  initialName?: string;
  /** Initial pipeline description for inline editing (optional — omit to hide description field) */
  initialDescription?: string;
  /**
   * Persist pipeline name and/or description. Called before stage save. Only
   * the fields the user actually changed are present in `updates`.
   */
  onPipelineDetailsSave?: (updates: {
    name?: string;
    description?: string;
  }) => Promise<void>;
}

// ── Tab configuration ────────────────────────────────────────────────────────

const PIPELINE_TABS = [
  { id: 'stages', label: 'Stages' },
  { id: 'card-layout', label: 'Card Layout' },
  { id: 'calculations', label: 'Calculations' },
];

// ── Stage data ───────────────────────────────────────────────────────────────

interface StageData extends StageItem {
  probability: number;
  order: number;
  description: string;
  last_event_seq: number;
  isNew?: boolean;
  is_terminal?: boolean;
  /**
   * Whether the server already has an explicit ahoy_is_terminal value stored
   * for this stage. Distinguishes "explicitly false" from "never set".
   */
  hasExplicitTerminal?: boolean;
  /**
   * Whether the user explicitly interacted with the Final toggle this session.
   * A stage with no stored value loads with is_terminal === false, so an
   * explicit-false choice can't be detected by comparing booleans alone. This
   * flag records the interaction so saving sends the explicit value (and
   * turning Final off actually persists).
   */
  terminalTouched?: boolean;
}

/**
 * New stages only send is_terminal when true, so a stage the user never
 * touched keeps the property unset (non-terminal) rather than being written
 * false. Existing stages send the explicit value when the user toggled the
 * Final control this session (tracked via terminalTouched — even a
 * net-unchanged toggle counts, so a stage can be explicitly turned off), when
 * the boolean changed, or when the server already stores an explicit value (a
 * safe no-op resend). Otherwise is_terminal is omitted so an edit to an
 * unrelated field can't clobber a stage's unset terminality.
 */
function buildIsTerminalPayload(
  stage: StageData,
  original: StageData | undefined
): { is_terminal: boolean } | Record<string, never> {
  if (stage.isNew) {
    return buildNewStageTerminalPayload(stage);
  }
  const userToggled =
    stage.terminalTouched ||
    !original ||
    stage.is_terminal !== original.is_terminal;
  if (userToggled || original?.hasExplicitTerminal) {
    return { is_terminal: stage.is_terminal ?? false };
  }
  return {};
}

/**
 * Small inline spinner for loading states.
 */
function SmallSpinner() {
  return (
    <svg
      className='animate-spin size-5 text-tertiary'
      viewBox='0 0 24 24'
      fill='none'
    >
      <circle
        className='opacity-25'
        cx='12'
        cy='12'
        r='10'
        stroke='currentColor'
        strokeWidth='4'
      />
      <path
        className='opacity-75'
        fill='currentColor'
        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
      />
    </svg>
  );
}

// =============================================================================
// Probability column definition — used when showProbability is true
// =============================================================================

function createProbabilityColumn(
  onProbabilityChange: (entityId: string, probability: number) => void,
  isSaving: boolean
): StageColumn<StageData> {
  return {
    id: 'probability',
    header: 'Win %',
    width: 'w-24',
    align: 'center',
    renderCell: (stage, { disabled }) => {
      const displayPercentage = Math.round(stage.probability * 100);

      const handleChange = (value: string) => {
        const numValue = parseInt(value, 10);
        if (isNaN(numValue)) {
          onProbabilityChange(stage.id, 0);
        } else {
          const clamped = Math.max(0, Math.min(100, numValue));
          onProbabilityChange(stage.id, clamped / 100);
        }
      };

      return (
        <InputGroup
          aria-label='Win probability'
          value={String(displayPercentage)}
          onChange={handleChange}
          isDisabled={disabled || isSaving}
          trailingAddon={
            <InputPrefix isDisabled={disabled || isSaving}>%</InputPrefix>
          }
        >
          <InputBase type='number' />
        </InputGroup>
      );
    },
    renderDragPreview: stage => (
      <span className='text-sm text-fg-quaternary'>
        {Math.round(stage.probability * 100)}%
      </span>
    ),
  };
}

// =============================================================================
// Field key helpers — composite keys for per-stage, per-field touch tracking
// =============================================================================

const makeFieldKey = (id: string, field: string) => `${id}:${field}`;
const makeNameFieldKey = (id: string) => makeFieldKey(id, 'name');

// =============================================================================
// StagesFormContent — inner component that accesses FormContext
//
// Separated from the outer component so it can call useFormContext() as a
// descendant of FormProvider. Renders the slideout body and footer.
// =============================================================================

interface StagesFormContentProps {
  title: string;
  description: string;
  stages: StageData[];
  columns: StageColumn<StageData>[];
  showProbability: boolean;
  initialName: string | undefined;
  inputPipelineName: string;
  onPipelineNameChange: (value: string) => void;
  initialDescription: string | undefined;
  inputDescription: string;
  onDescriptionChange: (value: string) => void;
  isLoading: boolean;
  isSaving: boolean;
  loadError: string | null;
  checkingUsageFor: string | null;
  usageErrors: Record<string, string>;
  announcement: string;
  isDirty: boolean;
  canEditStage: boolean;
  canDeleteStage: boolean;
  canCreateStage: boolean;
  onNameChange: (id: string, name: string) => void;
  onColorChange: (id: string, color: string | null) => void;
  onStageDescriptionChange: (id: string, description: string) => void;
  onDelete: (id: string) => Promise<void>;
  onAddStage: () => void;
  onReorder: (reordered: StageData[]) => void;
  onSave: (setSubmitError: (msg: string | null) => void) => Promise<void>;
  onCloseAttempt: () => void;
  recoveryBanner: string | null;
  onClearRecoveryBanner: () => void;
  /** Card layout tab props — only provided for pipeline-mode slideouts */
  cardLayoutTab?: CardLayoutTabProps;
  /** Calculations (stage footer) tab props — only for pipeline-mode slideouts */
  stageFooterTab?: StageFooterTabProps;
}

function StagesFormContent({
  title,
  description,
  stages,
  columns,
  showProbability,
  initialName,
  inputPipelineName,
  onPipelineNameChange,
  initialDescription,
  inputDescription,
  onDescriptionChange,
  isLoading,
  isSaving,
  loadError,
  checkingUsageFor,
  usageErrors,
  announcement,
  isDirty,
  canEditStage,
  canDeleteStage,
  canCreateStage,
  onNameChange,
  onColorChange,
  onStageDescriptionChange,
  onDelete,
  onAddStage,
  onReorder,
  onSave,
  onCloseAttempt,
  recoveryBanner,
  onClearRecoveryBanner,
  cardLayoutTab,
  stageFooterTab,
}: StagesFormContentProps) {
  const [activeTab, setActiveTab] = useState('stages');
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);
  const { getFieldProps, visibleErrors, setSubmitError, submitError } =
    useFormContext();

  // Full-width editable description beneath each stage row. Disabled while
  // saving or when the user lacks stage-edit permission.
  const renderStageDetail = useCallback(
    (stage: StageData) => (
      <StageDescriptionField
        stageId={stage.id}
        stageName={stage.name}
        value={stage.description}
        disabled={isSaving || !canEditStage}
        onChange={onStageDescriptionChange}
      />
    ),
    [isSaving, canEditStage, onStageDescriptionChange]
  );

  // Bridge FormContext blur tracking to StageListEditor's (id) signature
  const handleNameBlur = useCallback(
    (id: string) => {
      getFieldProps(makeFieldKey(id, 'name')).onBlur();
    },
    [getFieldProps]
  );

  // Convert FormContext visibleErrors to StageListEditor's validationErrors shape
  const validationErrors = useMemo<StageValidationErrors>(() => {
    const errors: StageValidationErrors = {};
    for (const stage of stages) {
      const nameError = visibleErrors[makeFieldKey(stage.id, 'name')];
      const probError = showProbability
        ? visibleErrors[makeFieldKey(stage.id, 'probability')]
        : undefined;
      if (nameError || probError) {
        errors[stage.id] = {};
        if (nameError) {
          errors[stage.id].name = nameError;
        }
        if (probError) {
          errors[stage.id].probability = probError;
        }
      }
    }
    return errors;
  }, [stages, visibleErrors, showProbability]);

  const handleSaveClick = useCallback(() => {
    onClearRecoveryBanner();
    onSave(setSubmitError);
  }, [onClearRecoveryBanner, onSave, setSubmitError]);

  const handleRetryClick = useCallback(() => {
    onClearRecoveryBanner();
    setSubmitError(null);
    onSave(setSubmitError);
  }, [onClearRecoveryBanner, setSubmitError, onSave]);

  const isCardLayoutActive = activeTab === 'card-layout' && !!cardLayoutTab;
  const isCalculationsActive = activeTab === 'calculations' && !!stageFooterTab;
  const isStagesActive = !isCardLayoutActive && !isCalculationsActive;

  return (
    <>
      <SlideoutMenu.Header onClose={onCloseAttempt}>
        <h2 className='text-lg font-semibold text-primary'>{title}</h2>
        <p className='text-sm text-tertiary mt-1'>{description}</p>
      </SlideoutMenu.Header>

      {/* Tab bar — only rendered when config tabs are available (pipeline mode) */}
      {cardLayoutTab && (
        <div className='flex-shrink-0 border-b border-[var(--color-border-secondary)] px-5'>
          <HorizontalTabs
            tabs={PIPELINE_TABS}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            noBorder
          />
        </div>
      )}

      <SlideoutMenu.Content>
        {isCardLayoutActive && cardLayoutTab && (
          <div className='py-4'>
            <CardFieldsEditorBody
              draftState={cardLayoutTab.draftState}
              properties={cardLayoutTab.properties}
              associations={cardLayoutTab.associations}
              entityLabel={cardLayoutTab.entityLabel}
              supplementalOptions={cardLayoutTab.supplementalOptions}
            />
          </div>
        )}

        {isCalculationsActive && stageFooterTab && (
          <div className='py-4'>
            <StageFooterEditorBody
              draftState={stageFooterTab.draftState}
              propertySchemas={stageFooterTab.propertySchemas}
              fieldTypeAggregations={stageFooterTab.fieldTypeAggregations}
            />
          </div>
        )}

        {isStagesActive && (
          <div className='space-y-4 py-4'>
            {/* Error Banner — load errors (loadError) or save errors (submitError) or post-refresh banner (recoveryBanner) */}
            {(loadError || submitError || recoveryBanner) && (
              <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
                <div className='flex items-start gap-3'>
                  <FeaturedIcon
                    icon={AlertCircle}
                    size='sm'
                    theme='light-outline'
                    color='error'
                  />
                  <div className='flex-1'>
                    <h4 className='text-sm font-medium text-utility-error-700'>
                      Error
                    </h4>
                    <p className='text-sm text-utility-error-600 mt-1'>
                      {loadError || submitError || recoveryBanner}
                    </p>
                  </div>
                  {submitError && !submitError.includes('were modified') && (
                    <Button
                      size='sm'
                      color='secondary'
                      iconLeading={RefreshCw01}
                      onClick={handleRetryClick}
                    >
                      Retry
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Pipeline name + description inputs */}
            {(initialName !== undefined ||
              initialDescription !== undefined) && (
              <div className='space-y-4 pb-4 border-b border-border-secondary'>
                {initialName !== undefined && (
                  <FormField name='pipelineName' label='Pipeline name'>
                    {({ isInvalid, showError }) => (
                      <InputGroup
                        aria-label='Pipeline name'
                        value={inputPipelineName}
                        onChange={onPipelineNameChange}
                        isDisabled={isSaving}
                        isInvalid={isInvalid}
                        showError={showError}
                      >
                        <InputBase placeholder='e.g., Sales Pipeline' />
                      </InputGroup>
                    )}
                  </FormField>
                )}

                {initialDescription !== undefined && (
                  <TextArea
                    label='Description'
                    value={inputDescription}
                    onChange={onDescriptionChange}
                    isDisabled={isSaving}
                    rows={3}
                    placeholder='Describe what belongs in this pipeline – your AI teammate uses this to route records to the right pipeline.'
                    hint='Explain who or what this pipeline is for. Visible to your team and your AI teammate.'
                  />
                )}
              </div>
            )}

            {/* Loading state */}
            {isLoading && (
              <div className='flex items-center justify-center py-8'>
                <SmallSpinner />
                <span className='ml-2 text-sm text-tertiary'>
                  Loading stages...
                </span>
              </div>
            )}

            {/* Stages list */}
            {!isLoading && (
              <StageListEditor<StageData>
                stages={stages}
                columns={columns}
                showColumnHeaders
                renderRowFooter={renderStageDetail}
                onNameChange={onNameChange}
                onNameBlur={handleNameBlur}
                onColorChange={onColorChange}
                onRemove={onDelete}
                onReorder={onReorder}
                onAdd={onAddStage}
                disabled={isSaving || !canEditStage}
                disableAdd={isSaving}
                hideDelete={!canDeleteStage}
                hideAdd={!canCreateStage}
                validationErrors={validationErrors}
                checkingUsageFor={checkingUsageFor}
                usageErrors={usageErrors}
                announcement={announcement}
                formFieldName={makeNameFieldKey}
              />
            )}
          </div>
        )}
      </SlideoutMenu.Content>

      <SlideoutMenu.Footer>
        {isCardLayoutActive && cardLayoutTab && (
          <div className='flex items-center justify-between'>
            <Button
              color='link-gray'
              size='sm'
              onClick={cardLayoutTab.onResetCardConfig}
            >
              Reset to default
            </Button>
            <div className='flex items-center gap-3'>
              <Button
                size='sm'
                color='secondary'
                onClick={onCloseAttempt}
                data-testid='card-layout-cancel'
              >
                Cancel
              </Button>
              <Button
                size='sm'
                color='primary'
                onClick={cardLayoutTab.onSaveCardConfig}
                isDisabled={!cardLayoutTab.draftState.isDirty}
                data-testid='card-layout-save'
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {isCalculationsActive && stageFooterTab && (
          <div className='flex items-center justify-between'>
            <Button
              color='link-gray'
              size='sm'
              onClick={stageFooterTab.onResetFooterConfig}
            >
              Reset to default
            </Button>
            <div className='flex items-center gap-3'>
              <Button
                size='sm'
                color='secondary'
                onClick={onCloseAttempt}
                data-testid='calculations-cancel'
              >
                Cancel
              </Button>
              <Button
                size='sm'
                color='primary'
                onClick={stageFooterTab.onSaveFooterConfig}
                isDisabled={!stageFooterTab.draftState.isDirty}
                data-testid='calculations-save'
              >
                Save
              </Button>
            </div>
          </div>
        )}

        {isStagesActive && (
          <div className='flex justify-end gap-3'>
            <Button
              size='sm'
              color='secondary'
              onClick={onCloseAttempt}
              data-testid='stages-slideout-cancel'
            >
              Cancel
            </Button>
            <SubmitButton
              size='sm'
              color='primary'
              onClick={handleSaveClick}
              isDisabled={isSaving || isLoading || !isDirty}
              data-testid='stages-slideout-save'
            >
              {isSaving ? 'Saving…' : 'Save'}
            </SubmitButton>
          </div>
        )}
      </SlideoutMenu.Footer>
    </>
  );
}

// =============================================================================
// StagesSlideout - Main component
// =============================================================================

export function StagesSlideout({
  config,
  isOpen,
  onClose,
  onSuccess,
  pipelineId: pipelineIdOverride,
  entityLabel,
  initialName,
  initialDescription,
  onPipelineDetailsSave,
}: StagesSlideoutProps) {
  useViewHealthScope('overlay', 'stages-slideout', isOpen);
  const {
    mode,
    stageEntityTypeName,
    associationTypeName,
    targetObjectTypeName,
    showProbability,
    title,
    description,
  } = config;
  // Pipeline mode carries an extra discriminator field (pipelineName) that
  // the stage_only arm doesn't have. Pull it out separately to keep the
  // shared destructure type-safe.
  const pipelineName = config.mode === 'pipeline' ? config.pipelineName : null;

  const { currentOrg } = useOrg();
  const queryClient = useQueryClient();
  const orgId = currentOrg?.id;

  // Permission checks for stage operations
  const { hasPermission } = usePermissions();
  const canEditStage = hasPermission(
    stageEntityTypeName,
    'ahoy_property_updated'
  );
  const canDeleteStage = hasPermission(
    stageEntityTypeName,
    'ahoy_object_deleted'
  );
  const canCreateStage = hasPermission(
    stageEntityTypeName,
    'ahoy_object_created'
  );

  // Look up pipeline entity_id to create pipeline-to-stage associations.
  // Stage-only configs have no pipeline tier, so the lookup always resolves
  // to null and load/save logic branches on mode below.
  const { pipelines, getDefaultPipeline } = usePipelines(orgId);
  const pipelineEntityId = useMemo(() => {
    if (mode !== 'pipeline') {
      return null;
    }
    if (pipelineIdOverride) {
      return pipelineIdOverride;
    }
    if (!pipelineName) {
      return null;
    }
    const byName = pipelines.find(p => p.pipeline_name === pipelineName);
    if (byName?.entity_id) {
      return byName.entity_id;
    }
    const byType = pipelines.find(p => p.entity_type_name === pipelineName);
    if (byType?.entity_id) {
      return byType.entity_id;
    }
    const defaultPipeline = getDefaultPipeline();
    return defaultPipeline?.entity_id ?? null;
  }, [mode, pipelineIdOverride, pipelines, pipelineName, getDefaultPipeline]);

  // State
  const [stages, setStages] = useState<StageData[]>([]);
  const [originalStages, setOriginalStages] = useState<StageData[]>([]);
  const [deletedStageIds, setDeletedStageIds] = useState<Set<string>>(
    new Set()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [checkingUsageFor, setCheckingUsageFor] = useState<string | null>(null);
  const [usageErrors, setUsageErrors] = useState<Record<string, string>>({});
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [inputPipelineName, setInputPipelineName] = useState('');
  const [originalPipelineName, setOriginalPipelineName] = useState('');
  const [inputDescription, setInputDescription] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [recoveryBanner, setRecoveryBanner] = useState<string | null>(null);

  const clearRecoveryBanner = useCallback(() => setRecoveryBanner(null), []);

  // Ref snapshot of originalStages for merge logic inside loadStagesFromServer.
  const originalStagesRef = useRef<StageData[]>([]);
  useEffect(() => {
    originalStagesRef.current = originalStages;
  }, [originalStages]);

  // Ref snapshot of deletedStageIds so the 422 recovery merge can preserve
  // pending deletions without re-creating loadStagesFromServer on each change.
  const deletedStageIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    deletedStageIdsRef.current = deletedStageIds;
  }, [deletedStageIds]);

  // Check if form is dirty
  const isDirty = useMemo(() => {
    const isNameDirty =
      initialName !== undefined &&
      inputPipelineName.trim() !== originalPipelineName;
    if (isNameDirty) {
      return true;
    }
    const isDescriptionDirty =
      initialDescription !== undefined &&
      inputDescription.trim() !== originalDescription;
    if (isDescriptionDirty) {
      return true;
    }
    if (deletedStageIds.size > 0) {
      return true;
    }
    if (stages.length !== originalStages.length) {
      return true;
    }
    return stages.some(stage => {
      const original = originalStages.find(o => o.id === stage.id);
      if (!original) {
        return true;
      }
      if (stage.isNew) {
        return true;
      }
      return (
        stage.name !== original.name ||
        stage.probability !== original.probability ||
        stage.order !== original.order ||
        stage.color !== original.color ||
        stage.description !== original.description ||
        stage.is_terminal !== original.is_terminal ||
        Boolean(stage.terminalTouched)
      );
    });
  }, [
    stages,
    originalStages,
    deletedStageIds,
    initialName,
    inputPipelineName,
    originalPipelineName,
    initialDescription,
    inputDescription,
    originalDescription,
  ]);

  // =========================================================================
  // FormProvider config — field validation + current values
  // =========================================================================

  const fieldConfig = useMemo(() => {
    const config: Record<string, FieldConfig> = {};

    // Conditional pipeline name field
    if (initialName !== undefined) {
      config.pipelineName = {
        validate: (v: unknown) =>
          !String(v ?? '').trim() ? 'Pipeline name is required' : undefined,
      };
    }

    // Build stage name list for duplicate detection
    const stageNames = stages.map(s => s.name.toLowerCase().trim());

    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      config[makeFieldKey(stage.id, 'name')] = {
        validate: (v: unknown) => {
          const val = String(v ?? '').trim();
          if (!val) {
            return 'Name is required';
          }
          const lowerVal = val.toLowerCase();
          const isDuplicate = stageNames.some(
            (name, idx) => idx !== i && name === lowerVal
          );
          if (isDuplicate) {
            return 'Duplicate stage name';
          }
          return undefined;
        },
      };

      if (showProbability) {
        config[makeFieldKey(stage.id, 'probability')] = {
          validate: (v: unknown) => {
            const p = Number(v);
            if (p < 0 || p > 1) {
              return 'Must be 0-100%';
            }
            return undefined;
          },
        };
      }
    }

    return config;
  }, [stages, showProbability, initialName]);

  const formValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    if (initialName !== undefined) {
      values.pipelineName = inputPipelineName;
    }
    for (const stage of stages) {
      values[makeFieldKey(stage.id, 'name')] = stage.name;
      if (showProbability) {
        values[makeFieldKey(stage.id, 'probability')] = stage.probability;
      }
    }
    return values;
  }, [stages, showProbability, initialName, inputPipelineName]);

  const fieldLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    if (initialName !== undefined) {
      labels.pipelineName = 'Pipeline name';
    }
    for (const stage of stages) {
      const name = stage.name || '(unnamed)';
      labels[makeFieldKey(stage.id, 'name')] = `Stage "${name}"`;
      if (showProbability) {
        labels[makeFieldKey(stage.id, 'probability')] =
          `Probability for "${name}"`;
      }
    }
    return labels;
  }, [stages, showProbability, initialName]);

  // Load stages from server, optionally preserving user's pending edits.
  const loadStagesFromServer = useCallback(
    async (preserveLocalEdits: boolean = false) => {
      if (!orgId) {
        return;
      }
      setIsLoading(true);
      setLoadError(null);
      try {
        const client = createCRMObjectClient(orgId, stageEntityTypeName);
        const properties = [
          'ahoy_name',
          'ahoy_order',
          'ahoy_color',
          'ahoy_description',
          'ahoy_is_terminal',
        ];
        if (showProbability) {
          properties.push('ahoy_probability');
        }
        const listParams: {
          properties: string[];
          sort: string;
          order: 'asc';
          associated_with_entity_id?: string;
        } = {
          properties,
          sort: 'ahoy_order',
          order: 'asc',
        };
        if (mode === 'pipeline' && pipelineEntityId) {
          listParams.associated_with_entity_id = pipelineEntityId;
        }
        const response = await client.list(listParams);

        const rawStages: StageData[] = response.results.map(
          (obj: CrmObject) => {
            const props = obj.properties ?? {};
            return {
              id: obj.entity_id,
              name: String(getPropertyValue<string>(props.ahoy_name) || ''),
              probability: Number(
                getPropertyValue<number>(props.ahoy_probability) || 0
              ),
              order: Number(getPropertyValue<number>(props.ahoy_order) || 0),
              description: String(
                getPropertyValue<string>(props.ahoy_description) || ''
              ),
              last_event_seq: obj.last_event_seq,
              color: String(getPropertyValue<string>(props.ahoy_color) || ''),
              is_terminal:
                String(
                  getPropertyValue<string>(props.ahoy_is_terminal) || ''
                ) === 'true',
              hasExplicitTerminal:
                getPropertyValue(props.ahoy_is_terminal) != null,
            };
          }
        );

        // Deduplicate stages by name (keep the one with the highest last_event_seq)
        const seenNames = new Map<string, StageData>();
        for (const stage of rawStages) {
          const key = stage.name.toLowerCase().trim();
          const existing = seenNames.get(key);
          if (!existing || stage.last_event_seq > existing.last_event_seq) {
            seenNames.set(key, stage);
          }
        }
        const loadedStages = Array.from(seenNames.values()).sort(
          (a, b) => a.order - b.order
        );

        if (preserveLocalEdits) {
          // Merge fresh server data with the user's pending edits.
          const pendingDeletes = deletedStageIdsRef.current;
          setStages(currentStages => {
            const originalSnapshot = originalStagesRef.current;
            // Exclude stages the user has marked for deletion so pending
            // deletes aren't reintroduced from server data.
            const visibleLoaded = loadedStages.filter(
              s => !pendingDeletes.has(s.id)
            );
            const merged: StageData[] = visibleLoaded.map(freshStage => {
              const current = currentStages.find(c => c.id === freshStage.id);
              if (!current || current.isNew) {
                return freshStage;
              }
              const original = originalSnapshot.find(
                o => o.id === freshStage.id
              );
              const userEdited =
                !original ||
                current.name !== original.name ||
                current.probability !== original.probability ||
                current.order !== original.order ||
                current.color !== original.color ||
                current.description !== original.description ||
                current.is_terminal !== original.is_terminal ||
                Boolean(current.terminalTouched);
              return userEdited ? current : freshStage;
            });
            // Append new stages the user added that are not yet on the server.
            // Sort by `order` so user-applied drag-and-drop reorders survive
            // the merge (otherwise rows would snap back to server order).
            const newStages = currentStages.filter(c => c.isNew);
            return [...merged, ...newStages].sort((a, b) => a.order - b.order);
          });
          setOriginalStages(loadedStages);
          // Keep pending deletions for stages still present on the server.
          // Drop ids the server no longer has (already deleted elsewhere).
          setDeletedStageIds(prev => {
            const next = new Set<string>();
            for (const id of prev) {
              if (loadedStages.some(s => s.id === id)) {
                next.add(id);
              }
            }
            return next;
          });
          setUsageErrors({});
        } else {
          setStages(loadedStages);
          setOriginalStages(loadedStages);
          setDeletedStageIds(new Set());
          setUsageErrors({});
          if (initialName !== undefined) {
            setInputPipelineName(initialName);
            setOriginalPipelineName(initialName.trim());
          }
          if (initialDescription !== undefined) {
            setInputDescription(initialDescription);
            setOriginalDescription(initialDescription.trim());
          }
        }
      } catch (error) {
        console.error('Failed to load stages:', error);
        setLoadError("We couldn't load stages. Try again.");
        if (preserveLocalEdits) {
          throw error;
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      orgId,
      mode,
      stageEntityTypeName,
      pipelineEntityId,
      showProbability,
      initialName,
      initialDescription,
    ]
  );

  // Load stages when slideout opens
  useEffect(() => {
    if (!isOpen || !orgId) {
      return;
    }
    // Pipeline mode requires a resolved pipeline entity to filter stages.
    // Stage-only mode lists by entity_type_name alone — no parent needed.
    if (mode === 'pipeline' && !pipelineEntityId) {
      return;
    }

    loadStagesFromServer(false);
  }, [isOpen, orgId, mode, pipelineEntityId, loadStagesFromServer]);

  // Reset state when slideout closes
  useEffect(() => {
    if (!isOpen) {
      setShowDiscardConfirm(false);
      setCheckingUsageFor(null);
      setRecoveryBanner(null);
    }
  }, [isOpen]);

  // Handle pipeline name change
  const handlePipelineNameChange = useCallback((value: string) => {
    setInputPipelineName(value);
  }, []);

  // Handle pipeline description change
  const handleDescriptionChange = useCallback((value: string) => {
    setInputDescription(value);
  }, []);

  // Handle name change
  const handleNameChange = useCallback((id: string, name: string) => {
    setStages(prev => prev.map(s => (s.id === id ? { ...s, name } : s)));
    // Clear usage error for this stage — name change resolves the conflict that triggered it.
    setUsageErrors(prev => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  // Handle probability change
  const handleProbabilityChange = useCallback(
    (id: string, probability: number) => {
      setStages(prev =>
        prev.map(s => (s.id === id ? { ...s, probability } : s))
      );
    },
    []
  );

  // Handle color change
  const handleColorChange = useCallback((id: string, color: string | null) => {
    setStages(prev =>
      prev.map(s => (s.id === id ? { ...s, color: color ?? '' } : s))
    );
  }, []);

  // Handle is_terminal change. terminalTouched records the explicit user
  // interaction so an inferred-terminal stage (loaded as false with no stored
  // value) can still send an explicit false on save.
  const handleIsTerminalChange = useCallback(
    (id: string, isTerminal: boolean) => {
      setStages(prev =>
        prev.map(s =>
          s.id === id
            ? { ...s, is_terminal: isTerminal, terminalTouched: true }
            : s
        )
      );
    },
    []
  );

  // Handle stage description change
  const handleStageDescriptionChange = useCallback(
    (id: string, description: string) => {
      setStages(prev =>
        prev.map(s => (s.id === id ? { ...s, description } : s))
      );
    },
    []
  );

  // Handle delete with usage check — also cleans up FormProvider touched state
  const handleDelete = useCallback(
    async (id: string) => {
      if (!orgId) {
        return;
      }

      const stage = stages.find(s => s.id === id);
      if (!stage) {
        return;
      }

      // If it's a new stage (not yet saved), just remove it
      if (stage.isNew) {
        setStages(prev => prev.filter(s => s.id !== id));
        setAnnouncement(`${stage.name || 'Stage'} deleted`);
        return;
      }

      // Check usage
      setCheckingUsageFor(id);
      setUsageErrors(prev => {
        const { [id]: _removed, ...rest } = prev;
        return rest;
      });

      try {
        const client = createCRMObjectClient(orgId, targetObjectTypeName);
        const response = await client.list({
          filter_groups: [
            {
              filters: [
                {
                  association: associationTypeName,
                  operator: 'exact',
                  value: id,
                },
              ],
            },
          ],
          limit: 1,
        });

        if ((response.count ?? 0) > 0) {
          setUsageErrors(prev => ({
            ...prev,
            [id]: 'This stage is in use and cannot be deleted',
          }));
        } else {
          setStages(prev => prev.filter(s => s.id !== id));
          setDeletedStageIds(prev => new Set([...prev, id]));
          setAnnouncement(`${stage.name || 'Stage'} deleted`);
        }
      } catch (error) {
        console.error('Failed to check stage usage:', error);
        setUsageErrors(prev => ({
          ...prev,
          [id]: 'Unable to verify stage usage. Please try again.',
        }));
      } finally {
        setCheckingUsageFor(null);
      }
    },
    [orgId, stages, targetObjectTypeName, associationTypeName]
  );

  // Handle add new stage
  const handleAddStage = useCallback(() => {
    const newStage: StageData = {
      id: crypto.randomUUID(),
      name: '',
      probability: 0,
      order: stages.length > 0 ? Math.max(...stages.map(s => s.order)) + 1 : 0,
      description: '',
      last_event_seq: 0,
      isNew: true,
    };

    setStages(prev => [...prev, newStage]);
    setAnnouncement('New stage added');
  }, [stages]);

  // Handle reorder from StageListEditor
  const handleReorder = useCallback((reordered: StageData[]) => {
    const withUpdatedOrder = reordered.map((s, idx) => ({
      ...s,
      order: idx,
    }));
    setStages(withUpdatedOrder);
    setAnnouncement('Stage reordered');
  }, []);

  // Save logic — called from inner component with setSubmitError from FormContext
  const handleSave = useCallback(
    async (setError: (msg: string | null) => void) => {
      if (!orgId) {
        return;
      }
      // Pipeline mode requires a resolved pipeline entity. Stage-only mode
      // routes by stage entity type and has no parent to look up.
      if (mode === 'pipeline' && !pipelineEntityId) {
        return;
      }

      setIsSaving(true);

      // Save pipeline name + description first (only the changed fields).
      const nextName = inputPipelineName.trim();
      const nextDescription = inputDescription.trim();
      const nameChanged =
        initialName !== undefined && nextName !== originalPipelineName;
      const descriptionChanged =
        initialDescription !== undefined &&
        nextDescription !== originalDescription;
      if (onPipelineDetailsSave && (nameChanged || descriptionChanged)) {
        const updates: { name?: string; description?: string } = {};
        if (nameChanged) {
          updates.name = nextName;
        }
        if (descriptionChanged) {
          updates.description = nextDescription;
        }
        try {
          await onPipelineDetailsSave(updates);
          if (nameChanged) {
            setOriginalPipelineName(nextName);
          }
          if (descriptionChanged) {
            setOriginalDescription(nextDescription);
          }
        } catch (error) {
          console.error('Failed to save pipeline details:', error);
          setError("We couldn't save pipeline details. Try again.");
          setIsSaving(false);
          return;
        }
      }

      try {
        const originalStageById = new Map(
          originalStagesRef.current.map(o => [o.id, o])
        );
        const desiredStages = stages.map(stage => ({
          ...(stage.isNew ? {} : { entity_id: stage.id }),
          name: stage.name,
          probability: stage.probability,
          order: stage.order,
          color: stage.color || null,
          description: stage.description,
          ...buildIsTerminalPayload(stage, originalStageById.get(stage.id)),
        }));

        if (mode === 'pipeline') {
          await apiPut(
            `/api/orgs/${orgId}/crm-events/schemas/pipelines/${pipelineEntityId}/stages/`,
            { stages: desiredStages, entity_type: targetObjectTypeName }
          );
        } else {
          // Stage-only path — no pipeline_entity_id, no entity_type field.
          // Endpoint identifies the stage taxonomy via the URL parameter.
          await apiPut(
            `/api/orgs/${orgId}/crm-events/schemas/pipelines/by-stage-type/${stageEntityTypeName}/stages/`,
            { stages: desiredStages }
          );
        }

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['stages', orgId] }),
          queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] }),
        ]);

        onSuccess?.();
        onClose();
      } catch (error: unknown) {
        const err = error as {
          status?: number;
          statusCode?: number;
          data?: { detail?: string };
          response?: { detail?: string };
          message?: string;
        };
        console.error('Failed to save stages:', error);
        // 409: user must manually refresh; do not auto-refetch.
        if (err?.status === 409 || err?.statusCode === 409) {
          setError('Stages were modified. Please refresh and try again.');
        } else if (err?.status === 422 || err?.statusCode === 422) {
          const rawDetail =
            err?.response?.detail || err?.message || 'Validation error';
          const sanitized = sanitizeError(rawDetail);
          setError(sanitized);
          try {
            await loadStagesFromServer(true);
            // After refresh: FormProvider clears submitError on values change, so move
            // the combined banner to recoveryBanner (which is not auto-cleared).
            setRecoveryBanner(
              `${sanitized} Stages were refreshed – review your changes and try saving again.`
            );
          } catch {
            // Refresh failed: surface the sanitized detail in recoveryBanner so it
            // survives any FormProvider state churn that may follow.
            setRecoveryBanner(sanitized);
          }
        } else {
          setError("We couldn't save stages. Try again.");
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      orgId,
      mode,
      stages,
      queryClient,
      onSuccess,
      onClose,
      pipelineEntityId,
      stageEntityTypeName,
      targetObjectTypeName,
      onPipelineDetailsSave,
      initialName,
      inputPipelineName,
      originalPipelineName,
      initialDescription,
      inputDescription,
      originalDescription,
      loadStagesFromServer,
      setRecoveryBanner,
    ]
  );

  // =========================================================================
  // Card layout config — property/association hooks + card draft state
  // =========================================================================

  const { schema: propertySchemas } = usePropertySchema(
    orgId ?? 0,
    targetObjectTypeName
  );
  const { associationTypes, associationTypeMap, stageAssociationTypes } =
    useAssociationTypes(orgId);

  // Field-type aggregation eligibility for the calculations (stage footer) tab.
  const { data: fieldTypes } = useFieldTypes();
  const fieldTypeAggregations = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const ft of fieldTypes ?? []) {
      map[ft.field_type_name] = ft.aggregations;
    }
    return map;
  }, [fieldTypes]);

  // Derive pipeline entity type name and server-persisted card config from
  // the matched pipeline object so usePipelineCardConfig can read/write the
  // ahoy_card_config property on the CRM entity.
  const matchedPipeline = useMemo(() => {
    if (mode !== 'pipeline' || !pipelineEntityId) {
      return null;
    }
    return pipelines.find(p => p.entity_id === pipelineEntityId) ?? null;
  }, [mode, pipelineEntityId, pipelines]);

  const pipelineEntityTypeName = matchedPipeline?.entity_type_name;
  const serverCardConfig = matchedPipeline?.properties?.ahoy_card_config;

  const { config: cardConfig, updateConfig: updateCardConfig } =
    usePipelineCardConfig(
      orgId,
      targetObjectTypeName,
      propertySchemas,
      associationTypeMap,
      pipelineEntityId,
      pipelineEntityTypeName,
      serverCardConfig
    );

  const cardFieldOptions = useMemo(
    () =>
      buildFieldPickerOptions(
        targetObjectTypeName,
        propertySchemas,
        associationTypes,
        stageAssociationTypes
      ),
    [
      targetObjectTypeName,
      propertySchemas,
      associationTypes,
      stageAssociationTypes,
    ]
  );

  const cardDraftState = useCardFieldsDraft(cardConfig);

  // Re-seed card draft each time the slideout opens so it reflects the
  // latest persisted config (the hook's useState only captures the initial
  // value; subsequent opens need an explicit reset).
  const latestCardConfigRef = useRef(cardConfig);
  latestCardConfigRef.current = cardConfig;
  const resetCardDraft = cardDraftState.resetDraft;

  useEffect(() => {
    if (isOpen) {
      resetCardDraft(latestCardConfigRef.current);
    }
  }, [isOpen, resetCardDraft]);

  const handleSaveCardConfig = useCallback(() => {
    const draft = cardDraftState.getDraft();
    updateCardConfig(draft);
    cardDraftState.resetDraft(draft);
    toast.success('Card layout saved');
    // Card draft is now persisted and clean. If there are no other unsaved
    // changes (stages/name/description), close the slideout. If there ARE
    // unsaved form changes, stay open so the user can save or discard those
    // separately — showing a "discard" dialog right after a successful save
    // is confusing.
    if (!isDirty) {
      onClose();
    }
  }, [cardDraftState, updateCardConfig, isDirty, onClose]);

  const handleResetCardConfig = useCallback(() => {
    // Draft-only: load defaults into the editor without persisting. The
    // change is committed (and localStorage updated) only when the user
    // clicks Save — so Reset followed by Cancel keeps the original config.
    const defaultConfig = buildDefaultCardConfig(
      targetObjectTypeName,
      propertySchemas,
      associationTypeMap
    );
    cardDraftState.replaceDraft(defaultConfig);
  }, [
    targetObjectTypeName,
    propertySchemas,
    associationTypeMap,
    cardDraftState,
  ]);

  // Build supplemental options for configured-but-ineligible fields (e.g.
  // markdown properties like ahoy_content) so the preview renders them
  // instead of showing "Empty".
  const supplementalOptions = useMemo(
    () =>
      buildSupplementalPropertyOptions(
        cardDraftState.draft,
        propertySchemas,
        cardFieldOptions.properties
      ),
    [cardDraftState.draft, propertySchemas, cardFieldOptions.properties]
  );

  const cardLayoutTab = useMemo<CardLayoutTabProps | undefined>(() => {
    if (mode !== 'pipeline') {
      return undefined;
    }
    return {
      draftState: cardDraftState,
      properties: cardFieldOptions.properties,
      associations: cardFieldOptions.associations,
      onSaveCardConfig: handleSaveCardConfig,
      onResetCardConfig: handleResetCardConfig,
      entityLabel,
      supplementalOptions,
    };
  }, [
    mode,
    cardDraftState,
    cardFieldOptions,
    handleSaveCardConfig,
    handleResetCardConfig,
    entityLabel,
    supplementalOptions,
  ]);

  // =========================================================================
  // Calculations (stage footer) config — footer draft state
  // =========================================================================

  const isDealPipeline = targetObjectTypeName === 'ahoy_deal';
  const serverFooterConfig =
    matchedPipeline?.properties?.ahoy_stage_footer_config;

  const { config: footerConfig, updateConfig: updateFooterConfig } =
    usePipelineFooterConfig(
      orgId,
      targetObjectTypeName,
      isDealPipeline,
      propertySchemas,
      fieldTypeAggregations,
      pipelineEntityId,
      pipelineEntityTypeName,
      serverFooterConfig
    );

  const footerDraftState = useStageFooterDraft(footerConfig);

  // Re-seed the footer draft each time the slideout opens so it reflects the
  // latest persisted config.
  const latestFooterConfigRef = useRef(footerConfig);
  latestFooterConfigRef.current = footerConfig;
  const resetFooterDraft = footerDraftState.resetDraft;
  useEffect(() => {
    if (isOpen) {
      resetFooterDraft(latestFooterConfigRef.current);
    }
  }, [isOpen, resetFooterDraft]);

  const handleSaveFooterConfig = useCallback(() => {
    const draft = footerDraftState.getDraft();
    updateFooterConfig(draft);
    footerDraftState.resetDraft(draft);
    toast.success('Calculations saved');
    // Close only if there are no other unsaved changes (mirrors card layout).
    if (!isDirty) {
      onClose();
    }
  }, [footerDraftState, updateFooterConfig, isDirty, onClose]);

  const handleResetFooterConfig = useCallback(() => {
    // Draft-only: load the default (deal → Sum·Amount + Sum·Weighted Amount,
    // otherwise empty) into the editor without persisting. Committed only
    // when the user clicks Save.
    footerDraftState.replaceDraft(buildDefaultFooterConfig(isDealPipeline));
  }, [footerDraftState, isDealPipeline]);

  const stageFooterTab = useMemo<StageFooterTabProps | undefined>(() => {
    if (mode !== 'pipeline') {
      return undefined;
    }
    return {
      draftState: footerDraftState,
      propertySchemas,
      fieldTypeAggregations,
      onSaveFooterConfig: handleSaveFooterConfig,
      onResetFooterConfig: handleResetFooterConfig,
    };
  }, [
    mode,
    footerDraftState,
    propertySchemas,
    fieldTypeAggregations,
    handleSaveFooterConfig,
    handleResetFooterConfig,
  ]);

  // Combined dirty state: stages/name/description OR unsaved card layout /
  // calculations changes.
  const isAnythingDirty =
    isDirty || cardDraftState.isDirty || footerDraftState.isDirty;

  // Handle close with dirty check
  const handleCloseAttempt = useCallback(() => {
    if (isAnythingDirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  }, [isAnythingDirty, onClose]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
    onClose();
  }, [onClose]);

  const handleCancelDiscard = useCallback(() => {
    setShowDiscardConfirm(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCloseAttempt();
      }
    },
    [handleCloseAttempt]
  );

  // Build custom columns: probability (when enabled) and the Final toggle
  // (always — applies to both pipeline and stage-only modes).
  const columns = useMemo<StageColumn<StageData>[]>(() => {
    const cols: StageColumn<StageData>[] = [];
    if (showProbability) {
      cols.push(createProbabilityColumn(handleProbabilityChange, isSaving));
    }
    cols.push(createIsTerminalColumn(handleIsTerminalChange, isSaving));
    return cols;
  }, [
    showProbability,
    handleProbabilityChange,
    handleIsTerminalChange,
    isSaving,
  ]);

  return (
    <>
      <SlideoutMenu
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        isDismissable={!isAnythingDirty}
        minWidth={480}
        maxWidth={800}
        resizable
        instance='stages-edit'
        dialogAriaLabel={title}
      >
        {({ close: _close }) => (
          <FormProvider
            fields={fieldConfig}
            values={formValues}
            fieldLabels={fieldLabels}
          >
            <StagesFormContent
              title={title}
              description={description}
              stages={stages}
              columns={columns}
              showProbability={showProbability}
              initialName={initialName}
              inputPipelineName={inputPipelineName}
              onPipelineNameChange={handlePipelineNameChange}
              initialDescription={initialDescription}
              inputDescription={inputDescription}
              onDescriptionChange={handleDescriptionChange}
              isLoading={isLoading}
              isSaving={isSaving}
              loadError={loadError}
              checkingUsageFor={checkingUsageFor}
              usageErrors={usageErrors}
              announcement={announcement}
              isDirty={isDirty}
              canEditStage={canEditStage}
              canDeleteStage={canDeleteStage}
              canCreateStage={canCreateStage}
              onNameChange={handleNameChange}
              onColorChange={handleColorChange}
              onStageDescriptionChange={handleStageDescriptionChange}
              onDelete={handleDelete}
              onAddStage={handleAddStage}
              onReorder={handleReorder}
              onSave={handleSave}
              onCloseAttempt={handleCloseAttempt}
              recoveryBanner={recoveryBanner}
              onClearRecoveryBanner={clearRecoveryBanner}
              cardLayoutTab={cardLayoutTab}
              stageFooterTab={stageFooterTab}
            />
          </FormProvider>
        )}
      </SlideoutMenu>

      <ConfirmationDialog
        isOpen={showDiscardConfirm}
        title='Discard unsaved changes?'
        description='You have unsaved changes that will be lost if you close without saving.'
        variant='destructive'
        confirmLabel='Discard'
        cancelLabel='Keep editing'
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
      />
    </>
  );
}

// =============================================================================
// Pre-configured stage configs
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components -- config constant co-located with StagesSlideout component
export const dealStagesConfig: PipelineStagesSlideoutConfig = {
  mode: 'pipeline',
  stageEntityTypeName: 'ahoy_deal_pipeline_stage',
  pipelineName: 'Sales Pipeline',
  associationTypeName: 'ahoy_deal_to_deal_pipeline_stage',
  targetObjectTypeName: 'ahoy_deal',
  showProbability: true,
  title: 'Edit deal stages',
  description: 'Manage your sales pipeline stages',
};

// eslint-disable-next-line react-refresh/only-export-components -- config constant co-located with StagesSlideout component
export const lifecycleStagesConfig: StageOnlyStagesSlideoutConfig = {
  mode: 'stage_only',
  stageEntityTypeName: 'ahoy_contact_lifecycle_stage',
  associationTypeName: 'ahoy_contact_to_lifecycle_stage',
  targetObjectTypeName: 'ahoy_contact',
  showProbability: false,
  title: 'Edit lifecycle stages',
  description: 'Manage your contact lifecycle stages',
};
